import { isEqual, omit } from 'lodash';
import type {
  ReportEntityType,
  StoredReportDateFilter,
  StoredReportQuery
} from 'v5/api/report-templates/reportTemplates.api';
import {
  resourceQueryUtils,
  type ResourceBookedFilter,
  type ResourceSearchQuery
} from 'v5/api/resource/resource.api';
import type { FieldNameTypeMapping } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import { resourceSearchToV1 } from 'v5/core/resource-search/resourceSearchToV1';
import { resourceSearchOfV1 } from 'v5/core/resource-search/v1ResourceSearch';
import { isRecord } from 'v5/platform/lang/isRecord';

/** `relative` is a backend `RelativeDates` value, kept as stored when this client doesn't know it. */
export type ReportDateValue =
  | { field: string; kind: 'relative'; relative: string }
  | { field: string; kind: 'range'; start: string | null; end: string | null }
  | null;

export interface ReportUnsupportedFilter {
  key: string;
  filter: unknown;
}

export interface ReportEditorState {
  typeId: number | null;
  /** People only; the default search for other entity types. */
  resourceSearch: ResourceSearchQuery;
  /** People only: v1's `availability`, "has a booking in these statuses between the dates". */
  booked: ResourceBookedFilter | null;
  date: ReportDateValue;
  /** Stored conditions the editor can't express: kept verbatim unless removed from this list. */
  unsupported: ReportUnsupportedFilter[];
  /** Keys whose only operators the server skips (`s_with`, `e_with`): dropped on save. */
  ignored: string[];
}

type FilterEdit =
  | { type: 'set'; key: string; value: unknown }
  | { type: 'remove'; key: string };

const IGNORED_OPERATORS = new Set(['s_with', 'e_with']);

/** Read off a filter by the server or copied there by the v1 form: not conditions. */
const NOT_OPERATORS = new Set([
  'type',
  'dataType',
  'id',
  'custom_field_definition_type',
  'groups'
]);

const DEFAULT_RELATIVE = 'LAST_30';

function typeFilterKeyOf(entityType: ReportEntityType): string | null {
  switch (entityType) {
    case 'contact':
      return 'resource_type_id';
    case 'production':
      return 'production_type_id';
    case 'booking':
      return 'booking_type_id';
    case 'callout':
    case 'shortlist':
    case 'callsheet':
      return null;
  }
}

/** `null` for a filter that isn't an operator object (a bare array). */
function operatorsOf(filter: unknown): string[] | null {
  return isRecord(filter)
    ? Object.entries(filter)
        .filter(
          ([key, value]) => !NOT_OPERATORS.has(key) && value !== undefined
        )
        .map(([key]) => key.replace(/^\$/, ''))
    : null;
}

function isIgnored(filter: unknown): boolean {
  const operators = operatorsOf(filter);
  return (
    !!operators?.length &&
    operators.every(operator => IGNORED_OPERATORS.has(operator))
  );
}

/** `undefined` when the stored filter is no type the editor can show. */
function typeIdOf(filter: unknown): number | null | undefined {
  if (filter === undefined) return null;
  if (!isRecord(filter)) return undefined;
  const entries = Object.entries(filter);
  if (entries.length !== 1) return undefined;
  const [operator, value] = entries[0];
  switch (operator.replace(/^\$/, '')) {
    case 'eq':
      if (value === null || value === '') return null;
      return typeof value === 'number' ? value : undefined;
    case 'in':
      return Array.isArray(value) &&
        value.length === 1 &&
        typeof value[0] === 'number'
        ? value[0]
        : undefined;
    default:
      return undefined;
  }
}

function bookedOf(filter: unknown): ResourceBookedFilter | null {
  if (!isRecord(filter)) return null;
  const { from_date, to_date, status } = filter;
  return typeof from_date === 'string' &&
    typeof to_date === 'string' &&
    Array.isArray(status) &&
    status.every((id): id is number => typeof id === 'number')
    ? { startDate: from_date, endDate: to_date, statusIds: status }
    : null;
}

/** The export reads only the first entry, and no valid `date_field` means no date filter. */
function dateOf(dateFilters: StoredReportDateFilter[]): ReportDateValue {
  const first = dateFilters[0];
  if (!first || typeof first.date_field !== 'string' || !first.date_field) {
    return null;
  }
  return first.is_relative
    ? {
        field: first.date_field,
        kind: 'relative',
        relative: first.relative ?? DEFAULT_RELATIVE
      }
    : {
        field: first.date_field,
        kind: 'range',
        start: first.start ?? null,
        end: first.end ?? null
      };
}

/** Keeps keys this client doesn't know from the stored entry; drops the other kind's stale ones. */
function dateFiltersOf(
  date: ReportDateValue,
  stored: StoredReportDateFilter | undefined
): StoredReportDateFilter[] {
  if (date === null) return [];
  const unknownKeys = omit(stored, [
    'date_field',
    'is_relative',
    'relative',
    'start',
    'end'
  ]);
  switch (date.kind) {
    case 'relative':
      return [
        {
          date_field: date.field,
          is_relative: true,
          relative: date.relative,
          ...unknownKeys
        }
      ];
    case 'range':
      return [
        {
          date_field: date.field,
          is_relative: false,
          start: date.start,
          end: date.end,
          ...unknownKeys
        }
      ];
  }
}

/** Edited keys stay where they were; new ones go last; a later edit of a key wins. */
function applyEdits(
  filters: Record<string, unknown>,
  edits: FilterEdit[]
): Record<string, unknown> {
  const byKey = new Map(edits.map(edit => [edit.key, edit]));
  const kept = Object.entries(filters).flatMap(([key, value]) => {
    const edit = byKey.get(key);
    switch (edit?.type) {
      case undefined:
        return [[key, value]];
      case 'set':
        return [[key, edit.value]];
      case 'remove':
        return [];
    }
  });
  const added = [...byKey.values()].flatMap(edit =>
    edit.type === 'set' && !(edit.key in filters)
      ? [[edit.key, edit.value]]
      : []
  );
  return Object.fromEntries([...kept, ...added]);
}

/** A stored report query as the editor shows it. */
export function reportEditorStateOf(
  entityType: ReportEntityType,
  stored: StoredReportQuery
): ReportEditorState {
  const typeKey = typeFilterKeyOf(entityType);
  const entries = Object.entries(stored.filters);
  const ignored = entries.flatMap(([key, filter]) =>
    isIgnored(filter) ? [key] : []
  );
  const typeId = typeKey ? typeIdOf(stored.filters[typeKey]) : null;
  const unreadableType =
    typeKey && typeId === undefined
      ? [{ key: typeKey, filter: stored.filters[typeKey] }]
      : [];
  const rest = entries.filter(
    ([key]) => key !== typeKey && !ignored.includes(key)
  );
  const base = {
    typeId: typeId ?? null,
    date: dateOf(stored.date_filters),
    ignored
  };

  if (entityType !== 'contact') {
    return {
      ...base,
      resourceSearch: resourceQueryUtils.ofDefault(),
      booked: null,
      unsupported: [
        ...unreadableType,
        // `{}` (v1 leaves `created_at: {}`) is no condition: kept, not shown
        ...rest.flatMap(([key, filter]) =>
          operatorsOf(filter)?.length === 0 ? [] : [{ key, filter }]
        )
      ]
    };
  }

  const availability = rest.find(([key]) => key === 'availability')?.[1];
  const booked = bookedOf(availability);
  const searchFilters = rest.filter(([key]) => key !== 'availability');
  const { query, notApplied } = resourceSearchOfV1({
    filters: Object.fromEntries(searchFilters)
  });
  const notAppliedKeys = new Set(notApplied.map(part => part.field));
  return {
    ...base,
    resourceSearch: query,
    booked,
    unsupported: [
      ...unreadableType,
      ...rest.flatMap(([key, filter]) =>
        notAppliedKeys.has(key) || (key === 'availability' && !booked)
          ? [{ key, filter }]
          : []
      )
    ]
  };
}

function resourceEdits(
  before: ReportEditorState,
  state: ReportEditorState,
  typeKey: string | null,
  fieldTypes: FieldNameTypeMapping
): FilterEdit[] {
  const beforeFilters: Record<string, unknown> = before.resourceSearch.filters;
  const stateFilters: Record<string, unknown> = state.resourceSearch.filters;
  const changedKeys = [
    ...new Set([...Object.keys(beforeFilters), ...Object.keys(stateFilters)])
  ].filter(
    key =>
      key !== typeKey &&
      !isEqual(beforeFilters[key] ?? null, stateFilters[key] ?? null)
  );
  const converted = resourceSearchToV1(state.resourceSearch, fieldTypes);
  const notConverted = changedKeys.filter(key =>
    converted.notConverted.includes(key)
  );
  if (notConverted.length > 0) {
    throw new Error(
      `No stored shape for the report filters: ${notConverted.join(', ')}`
    );
  }
  const filterEdits = changedKeys.map(
    (key): FilterEdit =>
      key in converted.filters
        ? { type: 'set', key, value: converted.filters[key] }
        : { type: 'remove', key }
  );
  const bookedEdits: FilterEdit[] = isEqual(before.booked, state.booked)
    ? []
    : [
        state.booked
          ? {
              type: 'set',
              key: 'availability',
              value: {
                status: state.booked.statusIds,
                from_date: state.booked.startDate,
                to_date: state.booked.endDate
              }
            }
          : { type: 'remove', key: 'availability' }
      ];
  return [...filterEdits, ...bookedEdits];
}

/**
 * The query to save: the stored one with only what the editor changed (compared with
 * `reportEditorStateOf(original)`) written in the v1 shape; untouched and unsupported keys keep
 * their stored value and place, server-ignored keys are dropped. `fieldTypes` types a changed
 * custom field (`custom_field_definition_type`). Throws when a changed condition has no stored
 * shape: the editor must not offer it.
 */
export function storedQueryOf(
  entityType: ReportEntityType,
  original: StoredReportQuery,
  state: ReportEditorState,
  fieldTypes: FieldNameTypeMapping
): StoredReportQuery {
  const before = reportEditorStateOf(entityType, original);
  const typeKey = typeFilterKeyOf(entityType);
  const keptUnsupported = new Set(state.unsupported.map(({ key }) => key));
  const typeEdits: FilterEdit[] =
    typeKey && state.typeId !== before.typeId
      ? [
          state.typeId === null
            ? { type: 'remove', key: typeKey }
            : { type: 'set', key: typeKey, value: { eq: state.typeId } }
        ]
      : [];
  const edits: FilterEdit[] = [
    ...before.ignored.map((key): FilterEdit => ({ type: 'remove', key })),
    ...before.unsupported
      .filter(({ key }) => !keptUnsupported.has(key))
      .map(({ key }): FilterEdit => ({ type: 'remove', key })),
    ...typeEdits,
    ...(entityType === 'contact'
      ? resourceEdits(before, state, typeKey, fieldTypes)
      : [])
  ];
  const storedDate = original.date_filters[0];
  // the editor shows a relative date without a period as the default one, which the export lacks
  const storedDateLacksPeriod =
    storedDate?.is_relative === true &&
    (storedDate.relative === undefined || storedDate.relative === null);
  return {
    ...original,
    filters: applyEdits(original.filters, edits),
    date_filters:
      isEqual(state.date, before.date) && !storedDateLacksPeriod
        ? original.date_filters
        : dateFiltersOf(state.date, storedDate)
  };
}
